/**
 * useTax — Encapsulates tax calculation state and orchestration.
 *
 * Provides taxSummary state and a `calculateTax` function that:
 * 1. Fetches product descriptions, categories, and stores
 * 2. Builds the exact tax request payload
 * 3. Calls the OneSource tax API
 * 4. Normalizes the result into NormalizedTaxResult
 *
 * The checkout page delegates to this hook instead of inline orchestration.
 */
import { useState, useCallback } from 'react';

import {
  callTaxApiDirect,
  normalizeTaxResult,
  buildExactTaxRequest,
  fetchProductDescriptions,
  fetchCategories,
  fetchStores,
} from '../services/calculateCartTax.service';
import type { Cart, AddressInput, NormalizedTaxResult } from '../types/tax.types';

interface UseTaxReturn {
  /** Current normalized tax result, or null if not yet calculated. */
  taxSummary: NormalizedTaxResult | null;
  /** Directly set taxSummary (e.g. to null when clearing). */
  setTaxSummary: React.Dispatch<React.SetStateAction<NormalizedTaxResult | null>>;
  /**
   * Run the full tax calculation pipeline.
   * Returns the NormalizedTaxResult on success, or null on failure.
   */
  calculateTax: (
    cart: Cart,
    billingAddress: AddressInput,
    shippingAddress: AddressInput,
    userProfileId?: string
  ) => Promise<NormalizedTaxResult | null>;
  /** Clear the current tax summary (e.g. when editing billing address). */
  clearTax: () => void;
}

export const useTax = (): UseTaxReturn => {
  const [taxSummary, setTaxSummary] = useState<NormalizedTaxResult | null>(null);

  const calculateTax = useCallback(
    async (
      cart: Cart,
      billingAddress: AddressInput,
      shippingAddress: AddressInput,
      userProfileId = ''
    ): Promise<NormalizedTaxResult | null> => {
      try {
        // Fetch supplemental data for the tax request
        const [productDescriptions, categories, stores] = await Promise.all([
          fetchProductDescriptions(cart),
          fetchCategories(),
          fetchStores(),
        ]);

        // Build and send the tax request
        const taxBody = buildExactTaxRequest(
          cart,
          billingAddress,
          shippingAddress,
          productDescriptions,
          userProfileId,
          categories,
          stores
        );

        const taxResult = await callTaxApiDirect(taxBody);
        const normalized = normalizeTaxResult(taxResult, cart);

        setTaxSummary(normalized);
        return normalized;
      } catch (error) {
        console.error('[tax] Tax calculation failed:', error);
        setTaxSummary(null);
        return null;
      }
    },
    []
  );

  const clearTax = useCallback(() => {
    setTaxSummary(null);
  }, []);

  return { taxSummary, setTaxSummary, calculateTax, clearTax };
};
